/**
 * The region of the account signed in on this browser — nothing else. Written
 * only by RegionGuard, once the user is known, and cleared on sign-out; the
 * edge proxy only ever READS it, to answer "does this browser belong to an
 * account whose own region settles the question?".
 *
 * Deliberately not written when someone merely opens a region's URL. Visiting
 * /eg is a visit, not a decision to live there — recording it would pin a
 * Global visitor to Egypt for a year because they followed one link. With no
 * account, the IP decides, every time.
 *
 * "global" is a real value here even though it is never a URL segment: it says
 * "this account belongs to no region, leave it on the region-less site", which
 * is the one thing CF-IPCountry must not be allowed to override.
 */
export const REGION_COOKIE = "region";

export const REGION_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

/**
 * The value recorded for the Global/no-region cohort — the one value that means
 * "leave this visitor on the region-less site" rather than naming a region to
 * put them on. Mirrors `GLOBAL_REGION_SLUG` in ./region deliberately rather
 * than importing it: this module is loaded by the edge proxy, which has no
 * business pulling in that module's live /api/regions cache for one string.
 */
export const NO_REGION = "global";

/**
 * Records the signed-in account's region. `null` means the Global/no region
 * cohort, which is stored rather than left blank: the whole point is that a
 * Global account opening the site from a mapped country (a US account browsing
 * from Egypt, say) must NOT be sent to that country's region, and the proxy can
 * only know that if something says so before their JavaScript runs.
 *
 * Deliberately persistent. `auth_session` — the other signal the proxy has that
 * someone is signed in — is a session cookie, so it is gone the next time the
 * browser opens while the account (in localStorage) is still there; without
 * this, that first request of every new browser session would be geolocated
 * and bounced to the wrong region before RegionGuard could correct it.
 */
export function rememberRegionChoice(regionSlug: string | null): void {
  if (typeof document === "undefined") {
    return;
  }

  const value = (regionSlug || NO_REGION).toLowerCase();
  const secure = window.location.protocol === "https:" ? "; secure" : "";

  document.cookie = `${REGION_COOKIE}=${encodeURIComponent(value)}; path=/; max-age=${REGION_COOKIE_MAX_AGE}; samesite=lax${secure}`;
}

/**
 * Drops the pin, returning the browser to the guest rule: geolocate on the next
 * region-less request. Called when the session is cleared, because the value it
 * holds describes an ACCOUNT — most of all "global", which RegionGuard writes
 * for an account belonging to no region. Left behind after a sign-out, that
 * value would keep an anonymous visitor on the Global site forever, when a
 * visitor with no account is exactly who geolocation is meant to place.
 */
export function forgetRegionChoice(): void {
  if (typeof document === "undefined") {
    return;
  }

  document.cookie = `${REGION_COOKIE}=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT`;
}
